import React, { useState, useRef, useCallback, useEffect } from 'react';
import { func, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Bot, Send, X, Loader2 } from 'lucide-react';

import { browseWithAi, executeBrowseAction } from '../../../util/api';
import { isNeverGonnaGiveYouUp, RICKROLL_VIDEO_URL } from '../../../util/easterEgg';
import { normalizeConfidence } from '../../../components/ConfidenceBadge/ConfidenceBadge';

import css from './AiFilterPanel.module.css';

/**
 * Laurel — PCIFIC AI assistant for category pages.
 *
 * Lives in the filter sidebar. Connects to /api/ai-stream?task=browse.
 * Handles filtering, messaging sellers, comparing listings, price checks.
 * Hybrid: single-shot input that expands to chat when AI needs clarification.
 */
const AiFilterPanel = props => {
  const { onApplyFilters, currentCategory, currentFilters, onClearFilters, currentUser } = props;

  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [clarificationQuestions, setClarificationQuestions] = useState(null);

  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current();
    };
  }, []);

  // Auto-scroll to latest message — scoped to messagesArea container only
  useEffect(() => {
    if (isExpanded && messagesAreaRef.current && chatHistory.length > 0) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [chatHistory, aiResponse, isLoading, isExpanded]);

  const handleSubmit = useCallback(
    (e, overrideMessage) => {
      if (e) e.preventDefault();
      const message = overrideMessage || query.trim();
      if (!message || isLoading) return;
      if (isNeverGonnaGiveYouUp(message)) {
        window.location.href = RICKROLL_VIDEO_URL;
        return;
      }

      // Cancel previous stream
      if (abortRef.current) abortRef.current();

      setIsLoading(true);
      setIsExpanded(true);
      setAiResponse('');
      setPendingAction(null);
      setClarificationQuestions(null);

      // Build conversation history for multi-turn
      const history = chatHistory.map(m => ({
        role: m.role,
        content: m.text,
      }));

      // Add current message to chat
      setChatHistory(prev => [...prev, { role: 'user', text: message }]);
      if (!overrideMessage) setQuery('');

      // Build page context
      const context = {
        current_page: 'category',
        ...(currentCategory?.category1 && { category1: currentCategory.category1 }),
        ...(currentCategory?.category2 && { category2: currentCategory.category2 }),
        ...(currentCategory?.category3 && { category3: currentCategory.category3 }),
      };

      let responseText = '';

      abortRef.current = browseWithAi(message, {
        conversationHistory: history,
        context,
        callbacks: {
          onContent: text => {
            responseText += text;
            setAiResponse(responseText);
          },
          onFiltersApplied: (filters, summary) => {
            if (onApplyFilters && filters) {
              onApplyFilters(filters);
            }
          },
          onActionProposed: action => {
            setPendingAction(action);
          },
          onQuestions: questions => {
            setClarificationQuestions(questions);
          },
          onDone: event => {
            setIsLoading(false);
            if (responseText) {
              setChatHistory(prev => [
                ...prev,
                {
                  role: 'assistant',
                  text: responseText,
                  confidence: event?.confidence ?? null,
                  explanation: event?.explanation || null,
                },
              ]);
            }
          },
          onError: error => {
            setIsLoading(false);
            setAiResponse(`Something went wrong: ${error}`);
          },
        },
      });
    },
    [query, isLoading, chatHistory, currentCategory, onApplyFilters]
  );

  const handleConfirmAction = useCallback(async () => {
    if (!pendingAction) return;
    try {
      await executeBrowseAction([pendingAction]);
      setChatHistory(prev => [...prev, { role: 'assistant', text: 'Message sent to the seller.' }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { role: 'assistant', text: `Failed: ${err.message}` }]);
    }
    setPendingAction(null);
  }, [pendingAction]);

  const handleCancelAction = useCallback(() => {
    setPendingAction(null);
    setChatHistory(prev => [...prev, { role: 'assistant', text: 'Cancelled.' }]);
  }, []);

  const handleOptionClick = useCallback(
    option => {
      setClarificationQuestions(null);
      handleSubmit(null, option);
    },
    [handleSubmit]
  );

  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
    setAiResponse('');
    setChatHistory([]);
    setPendingAction(null);
    setClarificationQuestions(null);
  }, []);

  return (
    <div className={classNames(css.root, { [css.expanded]: isExpanded })}>
      {/* Header */}
      <div className={css.header}>
        <div className={css.headerLeft}>
          <Bot className={css.laurelIcon} size={18} />
          <span className={css.headerLabel}>Laurel</span>
        </div>
        <button
          className={classNames(css.collapseBtn, { [css.collapseBtnVisible]: isExpanded })}
          onClick={handleCollapse}
          type="button"
          tabIndex={isExpanded ? 0 : -1}
        >
          <X size={14} />
        </button>
      </div>

      {/* Expandable messages area — always in DOM for CSS grid animation */}
      <div className={classNames(css.expandable, { [css.expandableOpen]: isExpanded })}>
        <div className={css.expandableInner}>
          <div className={css.messagesArea} ref={messagesAreaRef}>
            {/* Welcome message when empty */}
            {chatHistory.length === 0 && !aiResponse && !isLoading && (
              <div className={css.welcomeMessage}>
                Hey! I can help you filter, compare listings, check prices, or message sellers.
              </div>
            )}

            {/* Chat history */}
            {chatHistory.map((m, i) => (
              <React.Fragment key={i}>
                <div
                  className={classNames(css.message, {
                    [css.userMessage]: m.role === 'user',
                    [css.aiMessage]: m.role === 'assistant',
                  })}
                >
                  {m.role === 'assistant' && (
                    <div className={css.messageAvatar}>
                      <Bot size={14} />
                    </div>
                  )}
                  <div
                    className={classNames(css.messageBubble, {
                      [css.userBubble]: m.role === 'user',
                      [css.aiBubble]: m.role === 'assistant',
                    })}
                  >
                    {m.text}
                  </div>
                </div>
                {m.role === 'assistant' && m.confidence != null && (
                  <div className={css.evidenceBanner}>
                    <span className={css.evidenceScore}>
                      {normalizeConfidence(m.confidence)}/10
                    </span>
                    {m.explanation && (
                      <span className={css.evidenceText}>{m.explanation}</span>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Streaming response */}
            {aiResponse && chatHistory[chatHistory.length - 1]?.role === 'user' && (
              <div className={classNames(css.message, css.aiMessage)}>
                <div className={css.messageAvatar}>
                  <Bot size={14} />
                </div>
                <div className={classNames(css.messageBubble, css.aiBubble)}>{aiResponse}</div>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && !aiResponse && (
              <div className={classNames(css.message, css.aiMessage)}>
                <div className={css.messageAvatar}>
                  <Bot size={14} />
                </div>
                <div className={css.loadingDots}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            {/* Clarification questions */}
            {clarificationQuestions && clarificationQuestions.length > 0 && (
              <div className={css.clarificationCard}>
                <p className={css.clarificationQuestion}>{clarificationQuestions[0].question}</p>
                {clarificationQuestions[0].options && (
                  <div className={css.optionChips}>
                    {clarificationQuestions[0].options.map((opt, i) => (
                      <button
                        key={i}
                        className={css.optionChip}
                        onClick={() => handleOptionClick(opt)}
                        type="button"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pending action confirmation (message_seller) */}
            {pendingAction && pendingAction.type === 'message_seller' && (
              <div className={css.confirmationCard}>
                <p className={css.confirmTitle}>Send these questions to the seller?</p>
                {pendingAction.questions && (
                  <ol className={css.questionList}>
                    {pendingAction.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ol>
                )}
                <div className={css.confirmActions}>
                  <button className={css.confirmBtn} onClick={handleConfirmAction} type="button">
                    Send
                  </button>
                  <button className={css.cancelBtn} onClick={handleCancelAction} type="button">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input — at the bottom, like a real chat */}
      <form className={css.inputRow} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className={css.input}
          type="text"
          placeholder={
            isExpanded ? 'Ask Laurel anything...' : 'Filter, compare, message sellers...'
          }
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => !isExpanded && setIsExpanded(true)}
          disabled={isLoading}
        />
        <button className={css.sendBtn} type="submit" disabled={!query.trim() || isLoading}>
          {isLoading ? <Loader2 className={css.spinner} size={16} /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
};

AiFilterPanel.propTypes = {
  onApplyFilters: func.isRequired,
  currentCategory: object,
  currentFilters: object,
  onClearFilters: func,
  currentUser: shape({
    id: shape({ uuid: string }),
  }),
};

export default AiFilterPanel;
